// js/core/period.js — the period P&L's one law: a window figure is
// cumulative-through-end minus cumulative-through-day-before-start,
// produced by the SAME engine job-to-date uses. This module owns the
// "as of D" clip of the feeds object and the fieldwise diff. It must
// stay pure: no DOM, no fetch, no Date.now() outside localDateStr.

import { footToCents } from './task-costs.js';

const pad = (n) => String(n).padStart(2, '0');

/** timestamptz → 'YYYY-MM-DD' in the LOCAL timezone (the admin's calendar). */
export function localDateStr(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

/** 'YYYY-MM-DD' minus one calendar day. UTC math so DST can never skip a day. */
export function dayBefore(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d - 1));
  return t.getUTCFullYear() + '-' + pad(t.getUTCMonth() + 1) + '-' + pad(t.getUTCDate());
}

/** 'YYYY-MM-DD' plus one calendar day — dayBefore's mirror, same UTC math. */
export function dayAfter(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 1));
  return t.getUTCFullYear() + '-' + pad(t.getUTCMonth() + 1) + '-' + pad(t.getUTCDate());
}

/** 'Mon Oct 12' of a 'YYYY-MM-DD': the one formatter of a day on the clock screens (a day sheet's title, a date past the week). */
export function dayLabel(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
}

/**
 * The day of an instant as the clock screens name an old open day, seen from `now` (both a timestamptz, a Date or ms):
 * 'yesterday', a bare weekday ('Fri') two to six days back, the date ('Wed Oct 14') past that, since a bare weekday a
 * week back would name two days. One rule for the worker's red notice ("You're still checked in since Fri 7:02") and
 * the admin's On site now row ("open since Fri 7:02"). Whole calendar days of the device's zone, counted on the UTC
 * dates so a DST night cannot move one; `now` is a parameter, this module reads no clock.
 */
export function dayName(ts, now) {
  const ymd = localDateStr(ts), ago = (Date.parse(localDateStr(now)) - Date.parse(ymd)) / 86400000;
  if (ago === 1) return 'yesterday';
  if (ago > 6) return dayLabel(ymd);
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short' });
}

/**
 * One calendar month as { from, to }, counted from `todayStr` ('YYYY-MM-DD',
 * the caller's own "today"): offset 0 is this month, -1 last month.
 *
 * THIS month stops at today, never at the month's last day: nothing has
 * happened after today, so "Showing Oct 1 – Oct 31" on October 1st names a
 * range the figures under it do not cover — on the screen, in the PDF title
 * and in both file names, which all read this one range. A month that is
 * already over runs to its last day. UTC math, same as dayBefore.
 */
export function monthRange(todayStr, monthOffset = 0) {
  const [y, m] = todayStr.split('-').map(Number);
  const ymd = (t) => t.getUTCFullYear() + '-' + pad(t.getUTCMonth() + 1) + '-' + pad(t.getUTCDate());
  const from = ymd(new Date(Date.UTC(y, m - 1 + monthOffset, 1)));
  const last = ymd(new Date(Date.UTC(y, m + monthOffset, 0)));
  return { from, to: last > todayStr && from <= todayStr ? todayStr : last };
}

/**
 * The percent a plain percent line had ON date D: latest history row with
 * set_on <= D, else 0. The 1900-01-01 backfill sentinel makes pre-history
 * progress "before any period" — visible in every cumulative, absent from
 * every window that starts in real time.
 */
export function pctAsOf(progressRows, lineId, dateStr) {
  let best = null;
  for (const r of progressRows || []) {
    if (r.line_id !== lineId || r.set_on > dateStr) continue;
    if (!best || r.set_on > best.set_on) best = r;
  }
  return best ? Number(best.percent) || 0 : 0;
}

/**
 * Filing a percent under a day that is not today ("the line reached 60% by
 * Sep 30", typed on Oct 3 — owner's decision, 2026-10-02). What the write
 * means, before anything is written:
 *
 *   rows     the line's dated history as it is on file ({set_on, percent}).
 *   setOn    the day the admin picked; percent the figure he typed.
 *
 * The line's own percent_complete is NOT "the last thing typed": it is the
 * LATEST entry by date, which is what keeps pctAsOf(today) equal to it — the
 * invariant job-to-date and a period ending today both rest on. So:
 *
 *   current  what percent_complete must read once (setOn, percent) is on file:
 *            the typed percent when nothing is dated after it, otherwise the
 *            later entry's percent — a past date never overrides what was
 *            declared after it.
 *   heldBy   the date of that later entry (null when the typed one is the
 *            latest), so the screen can say why the line did not move.
 *   nextOn   the date of the FIRST entry after setOn (null when there is
 *            none): the typed percent counts from setOn up to the day before
 *            it, so those are the only periods this write changes. With one
 *            later entry it is heldBy; with several it is an earlier date.
 *
 * A date after today is refused for the same invariant: the line would read a
 * percent that no period ending today contains. An entry already on file at
 * setOn is replaced by the write itself (one row per line per day).
 *
 * A MIS-DATED entry is corrected by filing the percent on the right day and
 * REMOVING the entry on the wrong one (pctWithout, below) — never by typing
 * another percent over the wrong day: 0% there zeroes the line when it is the
 * latest entry and digs a dip between two entries (a period ending inside it
 * prints negative revenue), and "the percent the line had before" holds the
 * line down when the wrong day is the later of the two.
 */
export function datedPercentPlan(rows, setOn, percent, todayStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(setOn || '')) return { ok: false, reason: 'date' };
  if (setOn > todayStr) return { ok: false, reason: 'future' };
  let latest = { set_on: setOn, percent }, next = null;
  for (const r of rows || []) {
    if (!r || !(r.set_on > setOn)) continue;
    if (r.set_on > latest.set_on) latest = r;
    if (!next || r.set_on < next) next = r.set_on;
  }
  return { ok: true, current: Number(latest.percent) || 0, heldBy: next ? latest.set_on : null, nextOn: next };
}

/**
 * Taking the entry dated setOn OFF the file: what the line's percent_complete
 * must read afterwards — the LATEST remaining entry by date, the same rule as
 * above, and 0 when nothing remains (a line with no history has declared no
 * progress). Removing an entry that is not the latest leaves the line where it
 * is and only changes the periods that entry used to start.
 */
export function pctWithout(rows, setOn) {
  let latest = null;
  for (const r of rows || []) {
    if (r && r.set_on !== setOn && (!latest || r.set_on > latest.set_on)) latest = r;
  }
  return latest ? Number(latest.percent) || 0 : 0;
}

// One sentinel for every undated row: it belongs to "before any period",
// the same place backfilled percent history lives.
const EPOCH = '1900-01-01';

/**
 * The instant a delivery request is DATED by, everywhere a date is asked of
 * it: the period clip below, and the date Job Costs prints on its row, in its
 * CSV and in its invoice modal — one answer, or a statement and a period view
 * of the same job disagree about which month an order belongs to.
 *
 * Material is money when it ARRIVES (owner's decision, 2026-10-02), so that is
 * delivered_at — stamped by the database the moment the status becomes
 * 'delivered' and cleared if it leaves. A request that has not arrived has
 * none and is dated by the day it was asked for (it carries no money; the date
 * only places its row). A pickup is born delivered on the day it was collected.
 */
export function deliveryDateOf(request) {
  const r = request || {};
  return r.delivered_at || r.requested_at || null;
}

/**
 * RATE HISTORY (owner's decision 12, 2026-10-02). Rates stay live: a period is
 * priced with what is on file TODAY (clipFeeds below hands every rate table
 * through untouched), so changing one restates every earlier period. Every
 * period view says so, and says when one last changed if the audit log knows.
 *
 * These are the tables the database logs for that purpose
 * (supabase/migrations/20261002190000_audit_money_tables.sql — a test pins the
 * two lists together). `job`: the row prices ONE project (named by its
 * project_id, or by its own id on `projects`); the others price every job of
 * the company. `updateOnly`: on a catalogue table only an UPDATE is a rate
 * changing — an INSERT there is a piece, an item or a worker being created.
 *
 * `profiles_sub_flag` is a label, not a table: the migration files a flip of
 * profiles.is_subcontractor under it (it moves all of a person's work between
 * "priced by hours" and "priced by production units"), apart from the
 * 'profiles' rows every other profile edit leaves, which are not read.
 *
 * The migration also logs three tables that are NOT here, for the trail only:
 * equipment_locations, equipment_costs and job_cost_entries. Their edits are
 * dated facts being corrected (a rental ended, a scaffold start date moved),
 * not a rate changing — counting them would put every routine gear move on
 * every job's "last changed".
 */
const RATE_SOURCES = {
  pay_rates: {},
  delivery_material_prices: {},
  equipment: { updateOnly: true },
  material_types: { updateOnly: true },
  delivery_materials: { updateOnly: true },
  workers: { updateOnly: true },
  profiles_sub_flag: { updateOnly: true },
  organizations: { updateOnly: true },
  project_material_rates: { job: true },
  project_contract_lines: { job: true },
  project_recipe_lines: { job: true },
  projects: { job: true, updateOnly: true },
};
export const RATE_TABLES = Object.keys(RATE_SOURCES);

/**
 * The local day a rate, price, recipe or overhead that prices these projects
 * last changed, or null when the log cannot tell (no rows, not readable, not
 * installed, or rows of another shape) — the caller then prints the plain
 * sentence. `rows` are audit_log rows { table_name, action, row_pk,
 * created_at, project_id }, any order; `projectIds` null = the whole company
 * (the "Every job" table), else the job and its sub-projects.
 */
export function lastRateChange(rows, projectIds = null) {
  const ids = projectIds ? new Set(projectIds) : null;
  let best = null;
  for (const r of Array.isArray(rows) ? rows : []) {
    const src = r && RATE_SOURCES[r.table_name];
    const at = src ? Date.parse(r.created_at) : NaN;
    if (isNaN(at)) continue;
    // INSERT / UPDATE / DELETE only: the migration's BASELINE snapshot is the
    // "before" of a first change, not a change.
    if (r.action !== 'UPDATE' && (src.updateOnly || (r.action !== 'INSERT' && r.action !== 'DELETE'))) continue;
    if (src.job && ids && !ids.has(r.table_name === 'projects' ? r.row_pk : r.project_id)) continue;
    if (best == null || at > best) best = at;
  }
  return best == null ? null : localDateStr(best);
}

/**
 * The money columns of ONE job's rows that the "Recent changes" panel on Job
 * Costs reads back (review D10), per audit_log table. Only these are compared,
 * so a row logged for a column the panel does not name reads as no change.
 */
// project_recipe_lines left 2026-11-02 with material_cost_method: material is
// what came in, so a recipe's amounts and prices are no longer money (it stays
// in RATE_SOURCES above — the recipe map files delivered items by task).
export const JOB_CHANGE_FIELDS = Object.freeze({
  project_contract_lines:   ['contract_amount', 'estimated_qty', 'earn_method'],
  project_material_rates:   ['revenue_rate', 'sub_rate'],
  project_sub_rate_changes: ['rate', 'effective_from'],
  job_cost_entries:         ['amount', 'incurred_on', 'category'],
  equipment_costs:          ['monthly_rate', 'start_date', 'end_date'],
  // A Rentals allocation: no project_id, it is matched by site code (site_name).
  equipment_locations:      ['start_date', 'quantity'],
  // material_cost_method left 2026-11-02: no longer read, so no longer money.
  projects:                 ['sub_block_rate', 'sub_brick_rate', 'sub_firestop_rate'],
});

/**
 * audit_log rows of one job → what changed, newest first:
 *   [{ id, at, actor, table, action, row, fields: [{ field, from, to }] }]
 * audit_row() stores the row AFTER a write (the row as it was, on DELETE), so a
 * value's "from" is the previous row of the same record — its BASELINE for the
 * first change. `from` is undefined when no earlier row exists (the record
 * predates the log). An UPDATE that moved none of JOB_CHANGE_FIELDS is left
 * out; BASELINE is never a change. `row` is the record (after; before on DELETE).
 */
export function moneyChanges(rows) {
  // 11 and "11.000" are one amount; a date or a word compares as text.
  const norm = v => (v == null ? null : v !== '' && Number.isFinite(Number(v)) ? Number(v) : String(v));
  // project_material_rates has no id: the job + item IS the record.
  const recKey = r => r.table_name + '|' + (r.table_name === 'project_material_rates'
    ? r.changed.material_type_id : r.row_pk);
  const last = new Map(), out = [];
  (Array.isArray(rows) ? rows.slice() : [])
    .filter(r => r && JOB_CHANGE_FIELDS[r.table_name] && r.changed)
    .sort((a, b) => (Date.parse(a.created_at) - Date.parse(b.created_at)) || (Number(a.id) - Number(b.id)))
    .forEach(r => {
      const cols = JOB_CHANGE_FIELDS[r.table_name], k = recKey(r), prev = last.get(k), cur = r.changed;
      if (r.action === 'DELETE') last.delete(k); else last.set(k, cur);
      let fields;
      if (r.action === 'INSERT') fields = cols.filter(f => cur[f] != null).map(f => ({ field: f, from: null, to: cur[f] }));
      else if (r.action === 'DELETE') fields = cols.filter(f => cur[f] != null).map(f => ({ field: f, from: cur[f], to: null }));
      else if (r.action === 'UPDATE') {
        fields = cols.filter(f => (prev ? norm(prev[f]) !== norm(cur[f]) : cur[f] != null))
          .map(f => ({ field: f, from: prev ? prev[f] : undefined, to: cur[f] }));
        if (!fields.length) return;
      } else return;                                   // BASELINE: the "before", never a change
      out.push({ id: r.id, at: r.created_at, actor: r.actor || null, table: r.table_name,
                 action: r.action, row: cur, fields });
    });
  return out.reverse();
}

/**
 * A NEW feeds object clipped to "on or before asOf". Dated rows filtered,
 * open/overrunning equipment ranges end-clipped, plain percent lines
 * rewritten to their history as of asOf. Everything undated passes through
 * by reference. The input is never mutated.
 *
 * `todayStr` ('YYYY-MM-DD') defaults to the real local today so existing
 * callers stay safe, but jcComputeProject passes the SAME local-Vancouver
 * "today" the equipment day-math already uses (_ecTodayStr) — one source
 * of truth for what day it actually is, independent of asOf.
 *
 * clipRange only answers "as of D"; it is NOT what keeps a future day from
 * being billed:
 *   - OPEN rows (no end_date) clip to min(asOf, todayStr), so a period whose
 *     `to` lands in the future (e.g. "This month"'s preset returns the
 *     month's LAST day) never hands on an end date that hasn't happened.
 *   - Rows with an EXPLICIT end_date > asOf substitute asOf, which may itself
 *     be in the future. That is fine: the day-math that prices the row stops
 *     at today on its own — _ecDaysBetween (js/admin/65-equipment-costs.js)
 *     since 2026-10-01, scaffoldDaysBetween (js/core/scaffold-rental.js)
 *     always. Both job-to-date and every as-of run go through that same
 *     clamp, so This Period + before = Job to Date holds for a rental with a
 *     planned return date still ahead, and none of them charges days that
 *     have not happened. A second clamp here would be redundant.
 */
export function clipFeeds(feeds, asOf, todayStr = localDateStr(new Date())) {
  const dateOk = (d) => (d || EPOCH) <= asOf;
  const clipRange = (rows) => (rows || [])
    .filter(r => !r.start_date || r.start_date <= asOf)
    .map(r => {
      if (!r.end_date) return { ...r, end_date: asOf < todayStr ? asOf : todayStr };
      return r.end_date > asOf ? { ...r, end_date: asOf } : r;
    });
  return {
    ...feeds,
    timeEntries: (feeds.timeEntries || []).filter(r => dateOk(r.work_date)),
    entries: (feeds.entries || []).filter(r => dateOk(r.incurred_on)),
    deliveriesRaw: (feeds.deliveriesRaw || []).filter(r => dateOk(localDateStr(deliveryDateOf(r)))),
    prodLines: (feeds.prodLines || []).filter(r => dateOk(r.production_reports?.report_date)),
    subReports: (feeds.subReports || []).filter(r => dateOk(r.report_date)),
    equipCosts: clipRange(feeds.equipCosts),
    scaffoldLocs: clipRange(feeds.scaffoldLocs),
    contractLines: (feeds.contractLines || []).map(l =>
      (l.earn_method !== 'quantity' && !l.continues_line_id)
        ? { ...l, percent_complete: pctAsOf(feeds.progressRows, l.id, asOf) }
        : l),
  };
}

/** {key: number} map diff over the union of keys. */
export function diffNumberMap(mapTo, mapBefore) {
  const out = {};
  const keys = new Set([...Object.keys(mapTo || {}), ...Object.keys(mapBefore || {})]);
  keys.forEach(k => { out[k] = (Number(mapTo?.[k]) || 0) - (Number(mapBefore?.[k]) || 0); });
  return out;
}

/**
 * Two cumulative summaries → the window's figures. Margins are RECOMPUTED
 * from the window's own earned/profit (a 0-1 fraction, matching
 * jcComputeProject's summary convention) — never diffed: percentages
 * do not subtract.
 *
 * Every TOTAL of a summary is whole cents (jcComputeProject rounds the costs;
 * job-value.js rounds earned per contract line), so their difference is whole
 * cents too — the rounding here only strips the float noise a subtraction
 * leaves (725000.26 - 556697.74 is not quite 168302.52 in binary), it never
 * moves a figure. That is what keeps before + period = to for Direct,
 * Overhead, Total, Earned and Profit, and Direct + Overhead = Total and
 * Earned − Total = Profit, true to the cent in the window as well.
 *
 * The GROUPS are the one thing that is not a difference of printed figures.
 * Each cumulative's perGroup is fitted to its own Direct, and the fit's spare
 * cent can land on a group at one date and on another at the next — so
 * subtracting the two fitted maps printed a cent (or minus a cent) on a group
 * in which nothing happened. The window therefore reads the UNFITTED topic
 * totals (summary.perGroupRaw), subtracts those, and is fitted once, to its
 * own Direct: a group with no activity is exactly $0.00, and the groups still
 * add up to the window's Direct. What is given up is per-group telescoping — a
 * group's "before + period" may sit one cent from its job-to-date cell. No
 * screen prints a per-group "before"; every total above still telescopes.
 * A summary with no perGroupRaw (an older caller) falls back to the plain
 * difference of the fitted maps.
 *
 * openEarned (what open contract lines earned) windows exactly like earned —
 * it is carried so "% of contract earned in this period" can leave it out of
 * the ratio the same way job to date does (Ordinex.jobValue.earnedOfContract).
 */
export function periodFigures(sumTo, sumBefore) {
  const n = (v) => Number(v) || 0;
  const d = (k) => n(sumTo?.[k]) - n(sumBefore?.[k]);
  const cents = (v) => Math.round(v * 100) / 100;
  const earned = cents(d('earned'));
  const profit = cents(d('profit'));
  const directSpent = cents(d('directSpent'));
  let perGroup;
  if (sumTo?.perGroupRaw && sumBefore?.perGroupRaw) {
    perGroup = footToCents(diffNumberMap(sumTo.perGroupRaw, sumBefore.perGroupRaw), directSpent);
  } else {
    perGroup = diffNumberMap(sumTo?.perGroup, sumBefore?.perGroup);
    Object.keys(perGroup).forEach(k => { perGroup[k] = cents(perGroup[k]); });
  }
  return {
    directSpent,
    overheadSpent: cents(d('overheadSpent')),
    totalSpent: cents(d('totalSpent')),
    earned, openEarned: cents(d('openEarned')), profit,
    marginPct: earned > 0 ? profit / earned : null,
    perGroup,
  };
}
